import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminService, type StudentStatus } from '../../services/admin.service';

const STATUS_FILTERS: Array<{ label: string; value: StudentStatus | '' }> = [
  { label: 'All', value: '' },
  { label: 'Active', value: 'active' },
  { label: 'At Risk', value: 'at-risk' },
  { label: 'Inactive', value: 'inactive' },
];

function StatusBadge({ status }: { status: StudentStatus }) {
  const styles: Record<StudentStatus, string> = {
    active:   'text-success bg-success/10 border-success/20',
    'at-risk': 'text-caution bg-caution/10 border-caution/20',
    inactive: 'text-on-surface-variant bg-surface-container border-outline-variant/20',
  };
  return (
    <span className={`inline-flex min-h-6 items-center justify-center rounded-full border px-2.5 text-[11px] font-semibold leading-none ${styles[status]}`}>
      {status === 'at-risk' ? 'At Risk' : status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function ProgressBar({ value }: { value: number }) {
  const tone = value >= 75 ? 'bg-success' : value >= 40 ? 'bg-primary' : 'bg-caution';
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex-1 h-2.5 rounded-full bg-surface-container-high overflow-hidden ring-1 ring-inset ring-outline-variant/10">
        <div
          className={`h-full ${tone} rounded-full transition-all duration-700 shadow-[0_0_8px_rgba(0,0,0,0.08)]`}
          style={{ width: `${value}%` }}
        />
      </div>
      <span className="text-[11px] font-bold text-on-surface w-8 text-right shrink-0 tabular-nums">
        {value}%
      </span>
    </div>
  );
}

function StudentNameCell({
  name,
  email,
  platformStatus,
}: {
  name: string | null;
  email: string;
  platformStatus: 'PENDING' | 'ACTIVE';
}) {
  const displayName = name ?? email.split('@')[0];
  const avatarFrame =
    platformStatus === 'ACTIVE'
      ? 'border-success shadow-[0_0_0_2px_rgba(34,197,94,0.14)]'
      : 'border-error shadow-[0_0_0_2px_rgba(239,68,68,0.14)]';
  const activationLabel = platformStatus === 'ACTIVE' ? 'Account activated' : 'Account not activated';
  return (
    <div className="flex items-center gap-3 min-w-0">
      <div
        className={`w-8 h-8 rounded-full bg-surface-container-high border-2 flex items-center justify-center shrink-0 ${avatarFrame}`}
        title={activationLabel}
        aria-label={activationLabel}
      >
        <span className="text-[11px] font-bold text-on-surface-variant uppercase">
          {(name ?? email)[0]}
        </span>
      </div>
      <div className="min-w-0">
        <span className={`text-sm font-semibold truncate block ${name ? 'text-on-surface' : 'text-on-surface-variant italic'}`}>
          {displayName}
        </span>
        {!name && (
          <span className="text-[10px] text-on-surface-variant/60 truncate block">{email}</span>
        )}
      </div>
    </div>
  );
}

function ScoreCell({ score }: { score: number | null }) {
  if (score == null) {
    return (
      <span className="inline-flex items-center gap-1 text-sm text-on-surface-variant/40 font-medium">
        <span className="material-symbols-outlined text-[14px]">remove</span>
        <span className="text-xs">N/A</span>
      </span>
    );
  }
  const color = score >= 70 ? 'text-success' : score >= 50 ? 'text-primary' : 'text-caution';
  return (
    <span className={`text-sm font-bold tabular-nums ${color}`}>
      {score}
    </span>
  );
}

function formatLastActive(lastActiveAt: string | null): string {
  if (!lastActiveAt) return 'Never';
  const days = Math.floor((Date.now() - new Date(lastActiveAt).getTime()) / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  return new Date(lastActiveAt).toLocaleDateString();
}

const LIMIT = 20;

export default function AdminStudentList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState(searchParams.get('search') ?? '');
  const [status, setStatus] = useState<StudentStatus | ''>((searchParams.get('status') as StudentStatus) ?? '');
  const [fromDate, setFromDate] = useState(searchParams.get('fromDate') ?? '');
  const [toDate, setToDate] = useState(searchParams.get('toDate') ?? '');
  const [page, setPage] = useState(1);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, status, fromDate, toDate]);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (debouncedSearch) params.search = debouncedSearch;
    if (status) params.status = status;
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    setSearchParams(params, { replace: true });
  }, [debouncedSearch, status, fromDate, toDate, setSearchParams]);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-students', debouncedSearch, status, fromDate, toDate, page],
    queryFn: () =>
      adminService.listStudents({
        search: debouncedSearch || undefined,
        status: status || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        page,
        limit: LIMIT,
      }),
  });

  const totalPages = data ? Math.ceil(data.total / LIMIT) : 0;

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="space-y-1">
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center gap-1 text-xs text-on-surface-variant hover:text-on-surface transition-colors mb-2"
        >
          <span className="material-symbols-outlined text-sm">arrow_back</span>
          Overview
        </button>
        <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-on-surface">Students</h2>
        <p className="text-on-surface-variant">
          {data ? `${data.total} student${data.total !== 1 ? 's' : ''}` : 'Loading…'}
        </p>
      </div>

      {/* Filters */}
      <div className="space-y-4">
        {/* Search */}
        <div className="relative max-w-sm">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant text-[18px]">
            search
          </span>
          <input
            type="text"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field pl-9 w-full"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface"
            >
              <span className="material-symbols-outlined text-[16px]">close</span>
            </button>
          )}
        </div>

        {/* Status chips + date range */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1.5">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setStatus(f.value)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  status === f.value
                    ? 'bg-primary text-on-primary shadow-sm'
                    : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="input-field text-xs py-1.5"
              title="Joined from"
            />
            <span className="text-on-surface-variant text-xs">—</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="input-field text-xs py-1.5"
              title="Joined to"
            />
            {(fromDate || toDate) && (
              <button
                onClick={() => { setFromDate(''); setToDate(''); }}
                className="text-xs text-on-surface-variant hover:text-on-surface"
              >
                <span className="material-symbols-outlined text-sm">close</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface-container-low rounded-2xl overflow-hidden border border-outline-variant/10">
        {/* Table header */}
        <div className="grid grid-cols-[2fr_2fr_1.5fr_1fr_1fr_1fr_1fr_1fr] gap-4 px-5 py-3 border-b border-outline-variant/15 text-[11px] font-semibold text-on-surface-variant uppercase tracking-wider">
          <span>Student</span>
          <span>Email</span>
          <span>Progress</span>
          <span>Algo</span>
          <span>Interviews</span>
          <span>Score</span>
          <span>Last Active</span>
          <span>Status</span>
        </div>

        {isLoading ? (
          <div className="space-y-px">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-14 bg-surface-container-high/50 animate-pulse" />
            ))}
          </div>
        ) : !data || data.data.length === 0 ? (
          <div className="py-16 text-center">
            <span className="material-symbols-outlined text-4xl text-on-surface-variant mb-3 block">
              person_search
            </span>
            <p className="text-on-surface-variant text-sm">No students found.</p>
          </div>
        ) : (
          <div>
            {data.data.map((student) => (
              <button
                key={student.id}
                onClick={() => navigate(`/admin/students/${student.id}`)}
                className="w-full grid grid-cols-[2fr_2fr_1.5fr_1fr_1fr_1fr_1fr_1fr] gap-4 px-5 py-3.5 border-b border-outline-variant/10 hover:bg-surface-container transition-colors text-left items-center"
              >
                <StudentNameCell
                  name={student.name}
                  email={student.email}
                  platformStatus={student.platformStatus}
                />
                <span className="text-sm text-on-surface-variant truncate">{student.email}</span>
                <ProgressBar value={student.trainingProgressPct} />
                <span className="text-sm text-on-surface font-medium tabular-nums">{student.algoSolved}</span>
                <span className="text-sm text-on-surface font-medium tabular-nums">{student.interviewCount}</span>
                <ScoreCell score={student.avgInterviewScore} />
                <span className="text-sm text-on-surface-variant">
                  {formatLastActive(student.lastActiveAt)}
                </span>
                <StatusBadge status={student.status} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-on-surface-variant">
            Page {page} of {totalPages} · {data?.total ?? 0} total
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 rounded-xl bg-surface-container text-sm font-medium text-on-surface-variant hover:bg-surface-container-high disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 rounded-xl bg-surface-container text-sm font-medium text-on-surface-variant hover:bg-surface-container-high disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
